/**
 * resilientFetch — Wrapper fetch avec retry automatique via IndexedDB (queue HTTP).
 *
 * Comportement :
 *  - Online : exécute le fetch normalement, retourne la Response.
 *  - Offline OU erreur réseau : queue la requête dans IndexedDB et retourne une
 *    Response synthétique { queued: true, offline_id } — l'appelant peut afficher
 *    « sauvegardé hors-ligne, sera synchronisé au retour de connexion ».
 *
 * Sync : `syncHttpQueue(token)` rejoue toutes les requêtes en attente. Appelée
 * automatiquement au retour de l'événement `online`.
 *
 * Idempotence :
 *  - Chaque requête est identifiée par un `key` optionnel (ex: `pdc-step1-<id>`)
 *    → le queue écrase le précédent enregistrement de la même clé (pas d'accumulation).
 */
import { openDB } from 'idb';
import { toast } from 'sonner';

const DB_NAME = 'greenlink_agent_offline';
const STORE = 'http_queue';

let _db = null;
async function _openDb() {
  if (_db) return _db;
  _db = await openDB(DB_NAME, 3);
  return _db;
}

const isOnline = () => (typeof navigator !== 'undefined' ? navigator.onLine !== false : true);

/**
 * Fetch résilient. Si offline, met la requête en file d'attente.
 *
 * @param {string} url         URL absolue
 * @param {RequestInit} opts   Fetch options (method, headers, body)
 * @param {object} extra       { key: 'pdc-step1-123' }  → dédoublonnage
 * @returns {Promise<Response|{queued: true, offline_id: string}>}
 */
export async function resilientFetch(url, opts = {}, extra = {}) {
  if (isOnline()) {
    try {
      const res = await fetch(url, opts);
      return res;
    } catch (_e) {
      // Erreur réseau (probablement offline non détecté) → queue
    }
  }
  const offlineId = await _queue(url, opts, extra.key || null);
  return { queued: true, offline_id: offlineId, ok: false, status: 0, statusText: 'queued-offline' };
}

async function _queue(url, opts, key) {
  const db = await _openDb();
  const id = key || (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
  await db.put(STORE, {
    id, key: key || id, url,
    method: opts.method || 'GET',
    headers: opts.headers || {},
    body: opts.body || null,
    timestamp: new Date().toISOString(),
    attempts: 0,
  });
  return id;
}

/** Nombre de requêtes en attente. */
export async function getHttpQueueSize() {
  const db = await _openDb();
  return db.count(STORE);
}

/** Retente toutes les requêtes en attente. Retourne { synced, failed }. */
export async function syncHttpQueue() {
  if (!isOnline()) return { synced: 0, failed: 0, pending: await getHttpQueueSize() };
  const db = await _openDb();
  const all = await db.getAll(STORE);
  let synced = 0, failed = 0;
  for (const item of all) {
    try {
      const res = await fetch(item.url, {
        method: item.method,
        headers: item.headers,
        body: item.body,
      });
      if (res.ok) {
        await db.delete(STORE, item.id);
        synced++;
      } else {
        await db.put(STORE, { ...item, attempts: (item.attempts || 0) + 1, last_error: `HTTP ${res.status}` });
        failed++;
      }
    } catch (e) {
      await db.put(STORE, { ...item, attempts: (item.attempts || 0) + 1, last_error: String(e) });
      failed++;
    }
  }
  return { synced, failed, pending: await getHttpQueueSize() };
}

/** Attache l'auto-sync sur l'événement `online`. À appeler une fois par app. */
let _autoSyncAttached = false;
export function attachAutoSync() {
  if (_autoSyncAttached || typeof window === 'undefined') return;
  _autoSyncAttached = true;
  window.addEventListener('online', async () => {
    const size = await getHttpQueueSize();
    if (size === 0) return;
    toast.info(`Reconnexion — synchronisation de ${size} requête(s) en attente…`);
    const r = await syncHttpQueue();
    if (r.synced > 0) toast.success(`${r.synced} requête(s) synchronisée(s)`);
    if (r.failed > 0) toast.warning(`${r.failed} requête(s) en erreur — nouvelle tentative au prochain online`);
  });
  // Tentative initiale au chargement si en ligne
  if (isOnline()) {
    setTimeout(() => { syncHttpQueue().catch(() => {}); }, 2000);
  }
}
